// Pure logic backing the Staff Alerts page (plan §4). Kept out of the React
// component so it runs in the repo's ts-jest node env (no jsdom/RTL) — the same
// logic-in-helpers convention as billingPageLogic.ts. Everything here is the
// "resolve destination + priority into the POST /api/nxhub/alerts payload, and
// validate it" step, plus the eligible-device filter for the picker source.
//
// Contract is pinned to the as-built backend (nervex_software
// MainServer/Controllers/NxHubAlertsController.cs + NxHubAlertsDTOs.cs):
//   - scope ∈ {'device','clinic'} (no 'room' in v1)
//   - the wire identifier is deviceSerial (the opaque Device.Serial), NOT the
//     DB GUID — passed through verbatim, never re-cased
//   - priority ∈ {'normal','urgent'}, tone ∈ {'default','urgent'},
//     light ∈ {'blue_flash','red_flash'}
//   - message ≤ 280 chars

export type AlertScope = 'device' | 'clinic';
export type AlertPriority = 'normal' | 'urgent';
export type AlertTone = 'default' | 'urgent';
export type AlertLight = 'blue_flash' | 'red_flash';

/** Mirrors the backend MaxMessageLength (NxHubAlertsController.cs). */
export const MAX_MESSAGE_LENGTH = 280;

/** Request body for POST /api/nxhub/alerts (NxHubAlertsDTOs.cs SendAlertRequest). */
export interface SendStaffAlertRequest {
    scope: AlertScope;
    /** Required iff scope === 'device'. The opaque device serial, not the DB id. */
    deviceSerial?: string;
    message?: string;
    priority: AlertPriority;
    tone: AlertTone;
    light: AlertLight;
}

/** One recipient's status row (SendAlertResponse.PerDeviceStatus), keyed by serial. */
export interface PerDeviceStatus {
    deviceSerial: string;
    status: string;
}

/** Response from POST /api/nxhub/alerts (NxHubAlertsDTOs.cs SendAlertResponse). */
export interface SendStaffAlertResponse {
    alertId: string;
    recipientCount: number;
    perDeviceStatus: PerDeviceStatus[];
}

/** The page's local form state, resolved into a request by resolveAlertRequest. */
export interface AlertDraft {
    scope: AlertScope;
    /** The serial chosen in the device picker (only meaningful for scope='device'). */
    deviceSerial: string | null;
    priority: AlertPriority;
    message: string;
    /** Advanced overrides; when unset the priority defaults drive tone/light. */
    toneOverride?: AlertTone;
    lightOverride?: AlertLight;
}

export type ResolveResult =
    | { ok: true; request: SendStaffAlertRequest }
    | { ok: false; error: string };

/**
 * The tone + light a priority implies by default (plan §3). The advanced
 * tone/light selectors override these; this is the one-click common case.
 */
export function defaultCueForPriority(priority: AlertPriority): { tone: AlertTone; light: AlertLight } {
    return priority === 'urgent'
        ? { tone: 'urgent', light: 'red_flash' }
        : { tone: 'default', light: 'blue_flash' };
}

/**
 * Is this device able to receive a staff alert? v1 = the NxHUB fleet, which the
 * backend enumerates by the `nxhub` serial prefix (NxHubAlertsController
 * ResolveTargetsAsync / NxHubStatusController:43). The serial is opaque and
 * case-sensitive for IDENTITY, but the family check is case-insensitive (and
 * never mutates the serial). Defensive: the org-status endpoint already returns
 * only NxHUB devices, but a non-NxHUB serial must never be selectable as a target.
 */
export function isAlertCapableDevice(serial: string | null | undefined): boolean {
    if (!serial) return false;
    return serial.toLowerCase().startsWith('nxhub');
}

/**
 * Narrow a device list to the alert-capable NxHUB devices for the picker source
 * (plan §2/§7). Generic over anything carrying a `serial`.
 */
export function filterAlertCapableDevices<T extends { serial: string }>(devices: T[]): T[] {
    return devices.filter((d) => isAlertCapableDevice(d.serial));
}

/**
 * Resolve the form state into a validated POST body, or an error string for the
 * inline note. Pure — no network, no React. (plan §4/§7)
 *
 *  - scope='device' requires a chosen, alert-capable deviceSerial; the serial is
 *    carried verbatim (never trimmed/re-cased — MQTT topics are case-sensitive).
 *  - scope='clinic' carries NO deviceSerial: the backend fans out to the org
 *    server-side, so the client must not enumerate the (paginated) device list.
 *  - tone/light come from the priority default unless explicitly overridden.
 *  - message is optional, trimmed of trailing-only emptiness, and length-capped.
 */
export function resolveAlertRequest(draft: AlertDraft): ResolveResult {
    const cue = defaultCueForPriority(draft.priority);
    const tone = draft.toneOverride ?? cue.tone;
    const light = draft.lightOverride ?? cue.light;

    const message = draft.message ?? '';
    if (message.length > MAX_MESSAGE_LENGTH) {
        return { ok: false, error: `Message must be at most ${MAX_MESSAGE_LENGTH} characters.` };
    }

    if (draft.scope === 'device') {
        const serial = draft.deviceSerial;
        if (!serial) {
            return { ok: false, error: 'Choose a device to alert.' };
        }
        if (!isAlertCapableDevice(serial)) {
            return { ok: false, error: 'That device cannot receive a staff alert.' };
        }
        return {
            ok: true,
            request: {
                scope: 'device',
                deviceSerial: serial,
                priority: draft.priority,
                tone,
                light,
                ...(message.trim() ? { message } : {}),
            },
        };
    }

    // scope === 'clinic': no device list on the wire (review fix — backend
    // broadcasts to the caller's org; counts come from totalFilteredDevices).
    return {
        ok: true,
        request: {
            scope: 'clinic',
            priority: draft.priority,
            tone,
            light,
            ...(message.trim() ? { message } : {}),
        },
    };
}

/**
 * May this caller send a CLINIC-WIDE alert? Mirrors the shipped backend, which no
 * longer admin-gates scope=clinic: any subscribed member who can send a single-
 * device alert may also page the whole clinic (NxHubAlertsController.SendAlert —
 * both scopes share the `nxhub:alert:send` gate, and the clinic recipient set is
 * scoped to the caller's own org, so it can never reach another org). The
 * `nxhub:alert:send` subscription gate is enforced server-side; reaching this page
 * already implies it, so the "Whole clinic" toggle is enabled for everyone here.
 *
 * Kept as a function (rather than inlining `true`) so the one place that decides
 * clinic eligibility stays explicit and testable, and so a future re-gating has a
 * single seam. The arg is accepted but unused.
 */
export function canSendClinicAlert(_user?: { isAdmin: boolean; isOwner?: boolean }): boolean {
    return true;
}

/**
 * Should the device picker keep paging the (paginated, capped-at-1000) NxHUB
 * fleet? The single-device target list must cover the WHOLE fleet, not just the
 * first page, or a hub past page 1 is silently unselectable (review fix). Keep
 * going while we've loaded fewer than the server's totalFilteredDevices and are
 * under the safety page cap.
 */
export function shouldFetchNextDevicePage(
    loaded: number,
    totalFiltered: number,
    pagesFetched: number,
    maxPages: number,
): boolean {
    return loaded < totalFiltered && pagesFetched < maxPages;
}

/** Human label for a delivery status value (AlertDeliveryStatus on the backend). */
export function deliveryStatusLabel(status: string): string {
    switch (status) {
        case 'Queued':
            return 'Queued';
        case 'Published':
            return 'Sent';
        case 'Delivered':
            return 'Delivered';
        case 'Played':
            return 'Played';
        case 'Failed':
            return 'Failed';
        case 'Expired':
            return 'Expired';
        default:
            return status;
    }
}
